// A PO's money, as two of its four parts (Mitch, 2026-11-04: What / When / Invoice / Paid). Invoice: its deposit
// (deposit_pct, due when the vendor acknowledges) and balance (due before it ships), what's billed of each, the bills on
// it, and the 3-way match: each line ordered × received × billed, with every difference in words; bills for its deposit,
// balance or what's been received are made here. Paid: what's paid, on its way and still to pay, and every payment.
import { AlertTriangle, Plus } from "lucide-react";
import { useState } from "react";
import {
  BILL_KIND_LABELS,
  BILL_STATUS_LABELS,
  BILL_STATUS_TONE,
  BILL_STATUSES,
  billState,
  DIFF_LABELS,
  MATCH_LABELS,
  MATCH_TONE,
  type MatchLine,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_TONE,
  type PartState,
  type PoBillPart,
  poBillPlan,
  poMatch,
} from "../../../../shared/bills.ts";
import type { Bill, Payment, Po } from "../../../../shared/db.gen.ts";
import { amount, dayOf, useBillIndex } from "../../lib/bills.ts";
import { shortDate } from "../../lib/costs.ts";
import { plural, qty } from "../../lib/format.ts";
import { can } from "../../lib/mutate.ts";
import { Badge, Button, cx } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { PaneLink, usePaneOpen } from "../../ui/SidePane.tsx";
import { Why } from "../../ui/Why.tsx";
import { NewBillDialog } from "../bills/NewBillDialog.tsx";

const STATE: Record<PartState, { label: string; tone: "neutral" | "accent" | "warn" | "ok" }> = {
  none: { label: "None", tone: "neutral" },
  not_billed: { label: "Not billed yet", tone: "neutral" },
  billed: { label: "Billed, not paid", tone: "accent" },
  scheduled: { label: "Being paid", tone: "warn" },
  paid: { label: "Paid", tone: "ok" },
};

type BillRow = Bill & { paid: number };
type MatchRow = MatchLine & { id: string };
type PayRow = { id: string; payment: Payment; bill: Bill; amount: number };

/** Invoice (the bills and the 3-way match) or Paid (the payments). */
export function PoBills({ po, part = "invoice" }: { po: Po; part?: "invoice" | "paid" }) {
  const ix = useBillIndex();
  const plan = poBillPlan(ix, po.id, dayOf);
  const m = poMatch(ix, po.id);
  const [making, setMaking] = useState<PoBillPart | null>(null);
  const openPane = usePaneOpen();
  if (!plan || !m) return null;
  if (part === "paid") return <PoPaid po={po} />;
  const cur = po.currency;
  const billRows: BillRow[] = plan.bills.map((b) => ({ ...b, paid: billState(ix, b.id).paid }));
  // A bill opens in the side pane (Ctrl/⌘-click its number for the Bills page).
  const billColumns: Column<BillRow>[] = [
    {
      key: "number",
      header: "Bill",
      width: "92px",
      value: (b) => Number(b.number.replace(/\D/g, "")) || 0,
      text: (b) => b.number,
      cell: (b) => (
        <PaneLink kind="bill" id={b.id} to={`/bills?tab=bills&status=all&bill=${b.id}`} className="font-mono text-[12px] font-medium hover:underline">
          {b.number}
        </PaneLink>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      width: "100px",
      value: (b) => BILL_KIND_LABELS[b.kind],
      cell: (b) => <span className="text-[12px] text-muted">{BILL_KIND_LABELS[b.kind]}</span>,
    },
    {
      key: "invoice",
      header: "Invoice",
      width: "minmax(120px,1fr)",
      value: (b) => b.invoice_number || b.description,
      cell: (b) => <span className="truncate text-[12px] text-muted">{b.invoice_number || b.description}</span>,
    },
    {
      key: "bill_date",
      header: "Date",
      width: "90px",
      value: (b) => b.bill_date ?? "",
      cell: (b) => <span className="text-[12px] text-muted">{shortDate(b.bill_date)}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "100px",
      value: (b) => BILL_STATUSES.indexOf(b.status),
      text: (b) => BILL_STATUS_LABELS[b.status],
      cell: (b) => <Badge tone={BILL_STATUS_TONE[b.status]}>{BILL_STATUS_LABELS[b.status]}</Badge>,
    },
    {
      key: "amount",
      header: "Amount",
      width: "110px",
      align: "right",
      value: (b) => Number(b.amount),
      cell: (b) => <span className="font-mono text-[12px]">{amount(Number(b.amount), b.currency)}</span>,
    },
    {
      key: "paid",
      header: "Paid",
      width: "110px",
      align: "right",
      cell: (b) => <span className="text-[12px] text-muted">{b.paid > 0 ? `${amount(b.paid, b.currency)} paid` : ""}</span>,
    },
  ];
  const matchRows: MatchRow[] = m.lines.map((l) => ({ ...l, id: l.poLine.id }));
  const goods = [
    `Goods ${amount(m.totals.billedGoods, cur)}`,
    m.totals.deposits ? `deposits ${amount(m.totals.deposits, cur)}` : "",
    m.totals.credits ? `deposits taken off −${amount(m.totals.credits, cur)}` : "",
    m.totals.charges ? `other charges ${amount(m.totals.charges, cur)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const matchColumns: Column<MatchRow>[] = [
    {
      key: "line",
      header: "#",
      width: "40px",
      align: "right",
      value: (l) => l.poLine.position,
      cell: (l) => <span className="text-faint">{l.poLine.position}</span>,
      total: () => null, // "Total" goes under the SKU: this column is too narrow for it
    },
    {
      key: "sku",
      header: "SKU",
      width: "minmax(100px,1fr)",
      value: (l) => l.poLine.sku,
      cell: (l) => <span className="truncate font-mono text-[12px]">{l.poLine.sku}</span>,
      total: () => "Total",
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(140px,2fr)",
      value: (l) => l.poLine.name,
      cell: (l) => <span className="truncate text-[12px] text-muted">{l.poLine.name}</span>,
      total: () => (
        <span className="truncate text-muted" title={goods}>
          {goods}
        </span>
      ),
    },
    { key: "ordered", header: "Ordered", width: "80px", align: "right", cell: (l) => <span className="font-mono text-[12px]">{qty(l.ordered)}</span> },
    {
      key: "received",
      header: "Received",
      width: "96px",
      align: "right",
      text: (l) => (l.rejected > 0 ? `${qty(l.received)} (−${qty(l.rejected)})` : l.received),
      cell: (l) => (
        <span className={cx("font-mono text-[12px]", l.received < Math.min(l.billed, l.ordered) && "text-warn")}>
          {qty(l.received)}
          {l.rejected > 0 && <span className="text-danger"> (−{qty(l.rejected)})</span>}
        </span>
      ),
    },
    {
      key: "billed",
      header: "Billed",
      width: "80px",
      align: "right",
      cell: (l) => (
        <span className={cx("font-mono text-[12px]", l.billed > l.ordered && "text-warn", l.billed < l.received && "text-accent-text")}>{qty(l.billed)}</span>
      ),
    },
    {
      key: "price",
      header: "PO price",
      width: "96px",
      align: "right",
      value: (l) => l.price ?? "",
      cell: (l) => <span className="font-mono text-[12px] text-muted">{l.price == null ? "—" : amount(l.price, cur)}</span>,
    },
    {
      key: "billedAmount",
      header: "Billed amount",
      width: "120px",
      align: "right",
      cell: (l) => (
        <span className="font-mono text-[12px]">
          <Why
            title={l.poLine.sku || l.poLine.name}
            lines={l.bills.map((b) => ({
              label: `${b.number}: ${qty(b.qty)} × ${b.price == null ? "—" : amount(b.price, cur)}`,
              value: amount(b.amount, cur),
            }))}
          >
            {amount(l.billedAmount, cur)}
          </Why>
        </span>
      ),
      // The PO's whole net (goods, deposits and charges), not only these lines'
      total: () => <span className="font-mono">{amount(m.totals.net, cur)}</span>,
    },
  ];
  const open = po.status !== "draft" && po.status !== "cancelled";
  const canBill = open && can("bill.create_from_po");
  const real = m.diffs.filter((d) => !d.waiting && d.kind !== "received_not_billed");
  // A line without a price can't be billed (billFromPo refuses it): the deposit and the whole PO wait for prices.
  const noPrices = <span className="text-[12px] text-warn">Set its prices first</span>;
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4">
      {po.status === "draft" && <div className="mb-3 rounded-md bg-surface-2 px-3 py-2 text-[12px] text-muted">A draft: bills come once it's sent.</div>}
      <div className={cx("grid gap-2", plan.depositPct > 0 ? "grid-cols-3" : "grid-cols-2")}>
        <Part
          title="PO total"
          value={amount(plan.total, cur)}
          sub={plan.unpriced ? `${plural(plan.unpriced, "line has", "lines have")} no price yet` : `${plural(plan.bills.length, "bill")} on it`}
        />
        {plan.depositPct > 0 && (
          <Part
            title={`Deposit ${plan.depositPct}%`}
            value={amount(plan.deposit.amount, cur)}
            state={plan.deposit.state}
            sub={plan.deposit.dueText}
            action={
              canBill &&
              plan.deposit.state === "not_billed" &&
              plan.unbilled >= plan.deposit.amount &&
              (plan.unpriced ? (
                noPrices
              ) : (
                <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setMaking("deposit")}>
                  Bill the deposit
                </Button>
              ))
            }
          />
        )}
        <Part
          title={plan.depositPct > 0 ? "Balance" : "The PO"}
          value={amount(plan.depositPct > 0 ? plan.balance.amount : plan.total, cur)}
          state={plan.balance.state}
          sub={plan.balance.dueText}
          action={
            canBill &&
            (plan.unbilled > 0 || plan.unpriced > 0) &&
            (plan.unpriced ? (
              noPrices
            ) : (
              <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setMaking("balance")}>
                {plan.depositPct > 0 && plan.deposit.billed > 0 ? "Bill the balance" : "Bill the whole PO"}
              </Button>
            ))
          }
        />
      </div>
      <div className="mt-2 flex items-center gap-3 text-[12px] text-muted">
        <span>
          Not billed yet: <span className="font-mono text-text">{amount(Math.max(0, plan.unbilled), cur)}</span>
        </span>
        {plan.receivedUnbilled > 0 && (
          <>
            <span>
              Received, not billed: <span className="font-mono text-text">{amount(plan.receivedUnbilled, cur)}</span>
            </span>
            {canBill && (
              <button type="button" className="text-accent-text hover:underline" onClick={() => setMaking("received")}>
                Bill what's been received
              </button>
            )}
          </>
        )}
      </div>

      <div className="mt-5 mb-1.5 flex items-center gap-2">
        <h3 className="text-[11px] font-medium tracking-wide text-faint uppercase">Bills</h3>
      </div>
      {!plan.bills.length ? (
        <div className="rounded-md border border-dashed border-line-strong px-3 py-3 text-[12px] text-muted">No bills on this PO yet.</div>
      ) : (
        <DataTable
          fit
          rows={billRows}
          columns={billColumns}
          onOpen={(b) => openPane("bill", b.id)}
          exportName="PO bills"
          pageFilters={false}
          rowNoun={["bill", "bills"]}
        />
      )}

      <div className="mt-5 mb-1.5 flex items-center gap-2">
        <h3 className="text-[11px] font-medium tracking-wide text-faint uppercase">3-way match</h3>
        <Badge tone={MATCH_TONE[m.status]}>{MATCH_LABELS[m.status]}</Badge>
      </div>
      <DataTable
        fit={20}
        rows={matchRows}
        columns={matchColumns}
        exportName="PO 3-way match"
        pageFilters={false}
        rowNoun={["line", "lines"]}
        defaultSort={{ key: "line" }}
        empty={<div className="p-4 text-center text-[12px] text-muted">No lines.</div>}
      />
      {m.diffs.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1 text-[12px]">
          {m.diffs.map((d) => (
            <li
              key={`${d.kind}:${d.poLineId ?? ""}:${d.billId ?? ""}`}
              className={cx("flex items-start gap-2", d.waiting || d.kind === "received_not_billed" ? "text-muted" : "")}
            >
              {!d.waiting && d.kind !== "received_not_billed" && <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warn" />}
              <span className="min-w-0 flex-1">
                <span className="font-medium">{d.waiting ? "Still coming" : DIFF_LABELS[d.kind]}:</span> {d.text}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!real.length && m.status !== "not_billed" && (
        <div className="mt-2 text-[12px] text-muted">PO, receipts and bills agree{m.status === "waiting" ? " so far" : ""}.</div>
      )}
      {making && <NewBillDialog poId={po.id} part={making} onClose={() => setMaking(null)} />}
    </div>
  );
}

/** Paid: what's paid of its bills, on its way and still to pay, and each payment; a bill opens beside it to pay it. */
function PoPaid({ po }: { po: Po }) {
  const ix = useBillIndex();
  const plan = poBillPlan(ix, po.id, dayOf);
  if (!plan) return null;
  const cur = po.currency;
  const states = plan.bills.filter((b) => b.status !== "void").map((b) => ({ bill: b, st: billState(ix, b.id) }));
  const sum = (f: (x: (typeof states)[number]) => number) => states.reduce((n, x) => n + f(x), 0);
  const rows: PayRow[] = states.flatMap(({ bill, st }) =>
    st.payments.map((p) => ({ id: `${p.payment.id}:${bill.id}`, payment: p.payment, bill, amount: p.amount })),
  );
  const columns: Column<PayRow>[] = [
    {
      key: "number",
      header: "Payment",
      width: "100px",
      value: (r) => r.payment.number,
      cell: (r) => <span className="font-mono text-[12px] font-medium">{r.payment.number}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      value: (r) => PAYMENT_STATUS_LABELS[r.payment.status],
      cell: (r) => <Badge tone={PAYMENT_STATUS_TONE[r.payment.status]}>{PAYMENT_STATUS_LABELS[r.payment.status]}</Badge>,
    },
    {
      key: "bill",
      header: "For bill",
      width: "100px",
      value: (r) => r.bill.number,
      cell: (r) => (
        <PaneLink kind="bill" id={r.bill.id} to={`/bills?tab=bills&status=all&bill=${r.bill.id}`} className="font-mono text-[12px] hover:underline">
          {r.bill.number}
        </PaneLink>
      ),
    },
    { key: "method", header: "How", width: "90px", value: (r) => PAYMENT_METHOD_LABELS[r.payment.method] },
    {
      key: "paid_on",
      header: "Paid on",
      width: "90px",
      value: (r) => r.payment.paid_on ?? "",
      cell: (r) => <span className="text-[12px] text-muted">{shortDate(r.payment.paid_on)}</span>,
    },
    {
      key: "reference",
      header: "Reference",
      width: "minmax(120px,1fr)",
      value: (r) => r.payment.reference,
      cell: (r) => <span className="truncate text-[12px] text-muted">{r.payment.reference}</span>,
    },
    {
      key: "amount",
      header: "Amount",
      width: "110px",
      align: "right",
      value: (r) => r.amount,
      cell: (r) => <span className="font-mono text-[12px]">{amount(r.amount, r.bill.currency)}</span>,
    },
  ];
  const toPay = states.filter((x) => x.st.due > 0 && x.bill.status !== "draft");
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4">
      {!plan.bills.length ? (
        <div className="rounded-md bg-surface-2 px-3 py-2 text-[12px] text-muted">
          Nothing billed yet, so nothing to pay: its bills come on Invoice{po.status === "draft" ? " once it's sent" : ""}.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Part
              title="Paid"
              value={amount(
                sum((x) => x.st.paid),
                cur,
              )}
              sub={`of ${amount(
                sum((x) => x.st.amount),
                cur,
              )} billed`}
            />
            <Part
              title="On its way"
              value={amount(
                sum((x) => x.st.pending),
                cur,
              )}
              sub="Payments sent, not landed yet"
            />
            <Part
              title="Still to pay"
              value={amount(
                sum((x) => x.st.due),
                cur,
              )}
              sub={toPay.length ? `${plural(toPay.length, "bill")}: open one to pay it` : "Nothing due"}
              action={
                toPay.length > 0 && (
                  <span className="flex flex-wrap gap-2 text-[12px]">
                    {toPay.map(({ bill }) => (
                      <PaneLink
                        key={bill.id}
                        kind="bill"
                        id={bill.id}
                        to={`/bills?tab=bills&status=all&bill=${bill.id}`}
                        className="text-accent-text hover:underline"
                      >
                        Pay {bill.number}
                      </PaneLink>
                    ))}
                  </span>
                )
              }
            />
          </div>
          <div className="mt-5 mb-1.5 flex items-center gap-2">
            <h3 className="text-[11px] font-medium tracking-wide text-faint uppercase">Payments</h3>
          </div>
          <DataTable
            fit
            rows={rows}
            columns={columns}
            defaultSort={{ key: "paid_on", desc: true }}
            exportName="PO payments"
            pageFilters={false}
            rowNoun={["payment", "payments"]}
            empty={<div className="p-4 text-center text-[12px] text-muted">No payments yet.</div>}
          />
        </>
      )}
    </div>
  );
}

function Part({ title, value, sub, state, action }: { title: string; value: string; sub: string; state?: PartState; action?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-line px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span className="text-[12px] text-muted">{title}</span>
        {state && state !== "none" && <Badge tone={STATE[state].tone}>{STATE[state].label}</Badge>}
      </div>
      <div className="mt-0.5 font-mono text-[15px] font-semibold">{value}</div>
      <div className="mt-0.5 text-[11px] text-muted">{sub}</div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
